/**
 * iOS / Android Tactile Haptics Engine
 * Provides subtle tactile feedback on mobile devices using navigator.vibrate
 * and graceful fallback for browsers without vibration support.
 */

class HapticsEngine {
  private isSupported(): boolean {
    return typeof window !== 'undefined' && 'navigator' in window && typeof window.navigator.vibrate === 'function';
  }

  /**
   * Ultra-subtle selection tick (e.g. changing segmented control tab)
   */
  selection(): void {
    if (this.isSupported()) {
      try {
        window.navigator.vibrate(8);
      } catch {
        // Ignore safety errors in browsers with strict user-gesture policies
      }
    }
  }

  /**
   * Light tactile tap (e.g. tapping a button or card)
   */
  tap(): void {
    if (this.isSupported()) {
      try {
        window.navigator.vibrate(14);
      } catch {}
    }
  }

  impactLight(): void {
    this.tap();
  }

  /**
   * Medium impact (e.g. opening a sheet modal, triggering a remote ring)
   */
  impact(): void {
    if (this.isSupported()) {
      try {
        window.navigator.vibrate(28);
      } catch {}
    }
  }

  impactMedium(): void {
    this.impact();
  }

  impactHeavy(): void {
    if (this.isSupported()) {
      try {
        window.navigator.vibrate(45);
      } catch {}
    }
  }

  notificationSuccess(): void {
    if (this.isSupported()) {
      try {
        window.navigator.vibrate([15, 40, 20]);
      } catch {}
    }
  }

  notificationError(): void {
    if (this.isSupported()) {
      try {
        window.navigator.vibrate([40, 50, 40, 50, 40]);
      } catch {}
    }
  }

  /**
   * Distinctive double-pulse alert resonance (e.g. when an inactivity alert triggers)
   */
  alert(): void {
    if (this.isSupported()) {
      try {
        window.navigator.vibrate([35, 45, 35]);
      } catch {}
    }
  }
}

export const haptics = new HapticsEngine();
